// src/components/roads/ponding/useRoadPondingForecast.js
// Loads the road ponding forecast, follows the live push (`road_ponding_forecast` — recomputed after every drainage
// outlook run) and drives hour playback for the map. Calls onEscalate(newSignal, data) when the signal gets worse.
import { useCallback, useEffect, useRef, useState } from 'react';
import webSocketService from '../../../services/websocketService';
import { getPondingForecast } from './pondingApi';
import { SIGNAL_RANK } from './pondingUi';

const PLAY_MS = 900;

export default function useRoadPondingForecast({ enabled = true, onEscalate } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stale, setStale] = useState(false);
    const [hour, setHour] = useState(null);          // null = peak of the window
    const [playing, setPlaying] = useState(false);
    const seq = useRef(0);
    const signalRef = useRef(null);
    const escalateRef = useRef(onEscalate); escalateRef.current = onEscalate;

    const load = useCallback(async (force = false) => {
        const my = ++seq.current;
        setLoading(true);
        try {
            const res = await getPondingForecast({ force, hourly: true });
            if (my !== seq.current) return;
            const d = res?.data || null;
            setData(d); setStale(!!res?.stale); setError(null);
            if (d) {
                const prev = signalRef.current;
                if (prev != null && SIGNAL_RANK[d.signal] > SIGNAL_RANK[prev]) escalateRef.current?.(d.signal, d);
                signalRef.current = d.signal;
            }
        } catch (e) {
            if (my === seq.current) setError(e?.message || 'Road ponding forecast unavailable.');
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        load(false);
        let t = null;
        const onPush = (msg) => {
            const p = msg?.payload ?? msg?.data ?? msg;
            if (!p || !p.summary) return;
            // the push is compact (no hourly series) — refetch the full forecast, debounced
            clearTimeout(t); t = setTimeout(() => load(false), 800);
        };
        const off = webSocketService.subscribe?.('road_ponding_forecast', onPush);
        return () => { clearTimeout(t); if (typeof off === 'function') off(); else webSocketService.unsubscribe?.('road_ponding_forecast', onPush); };
    }, [enabled, load]);

    // hour playback
    useEffect(() => {
        if (!playing || !data?.horizon_hours) return undefined;
        const id = setInterval(() => setHour((h) => {
            const next = h == null ? 0 : h + 1;
            if (next >= data.horizon_hours) { setPlaying(false); return null; }
            return next;
        }), PLAY_MS);
        return () => clearInterval(id);
    }, [playing, data?.horizon_hours]);

    return { data, loading, error, stale, refresh: load, hour, setHour, playing, setPlaying };
}
